import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useLogout } from '../hooks/useLogout';
import { useUser } from '../hooks/useUser';
import { LanguageSwitcher } from '../components/shared/LanguageSwitcher';
import {
  LayoutDashboard,
  Package,
  Truck,
  Users,
  LogOut,
  Menu,
  ShieldAlert
} from 'lucide-react';
import { Button } from '../components/ui/button';

export const AdminLayout: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { handleLogout } = useLogout();
  const { user } = useUser();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const navItems = [
    {
      path: '/admin',
      label: t('admin.nav.dashboard'),
      icon: <LayoutDashboard className="h-4 w-4" />
    },
    {
      path: '/admin/quotes',
      label: t('admin.nav.quotes'),
      icon: <Package className="h-4 w-4" />
    },
    {
      path: '/admin/companies',
      label: t('admin.nav.companies'),
      icon: <Truck className="h-4 w-4" />
    },
    {
      path: '/admin/clients',
      label: t('admin.nav.clients'),
      icon: <Users className="h-4 w-4" />
    },
    {
      path: '/admin/users',
      label: t('admin.nav.users'),
      icon: <ShieldAlert className="h-4 w-4" />
    },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-slate-100 transform transition-transform duration-200 ease-in-out lg:sticky lg:top-0 lg:bottom-auto lg:h-screen lg:translate-x-0 flex flex-col flex-none
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-slate-800">
          <h1 className="text-xl tracking-wider">
            {t('app.title')}{' '}
            <span className="text-xs bg-slate-800 px-2 py-1 rounded text-slate-400 ml-2">
              {t('admin.nav.admin_label')}
            </span>
          </h1>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-slate-700 flex items-center justify-center text-slate-300">
              {user?.name ? user.name.charAt(0).toUpperCase() : t('admin.nav.user_fallback_initial')}
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-white truncate">{user?.name || t('admin.nav.user_fallback_name')}</p>
              <p className="text-xs text-slate-400 truncate capitalize">{user?.role || t('admin.nav.user_fallback_role')}</p>
            </div>
          </div>
        </div>

        {/* Nav Links */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors
                  ${isActive
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
                `}
                onClick={() => setIsSidebarOpen(false)}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Footer Actions */}
        <div className="shrink-0 p-4 border-t border-slate-800 space-y-4">
          <div className="flex justify-between items-center px-2">
            <span className="text-xs text-slate-500 uppercase">{t('common.language')}</span>
            <div className="scale-90 origin-right">
              <LanguageSwitcher />
            </div>
          </div>

          <Button
            variant="destructive"
            className="w-full justify-start text-red-100 hover:text-white bg-red-900/20 hover:bg-red-900/40 border-none"
            onClick={handleLogout}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('auth.logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:h-screen">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between bg-card border-b border-border px-4 py-3 shadow-sm">
          <Button variant="ghost" size="icon" onClick={() => setIsSidebarOpen(true)}>
            <Menu className="h-6 w-6 text-muted-foreground" />
          </Button>
            <span className="text-foreground">
              {t('app.title')} {t('admin.nav.admin_label')}
            </span>
          <div className="w-10"></div> {/* Spacer */}
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
